import { produce } from 'immer';
import { isNilOrEmptyString } from '../utils/utils.esm.js';
import { actions } from './actions.esm.js';
import { init } from './reducers/init.esm.js';

const initialState = {};
const defaultFilterState = {
    filters: {},
    displayedFilters: [],
    defaultFilters: [],
    initialized: false,
};
const reducer = produce(reducerFunction);
function reducerFunction(draft, action) {
    const { name } = action.payload;
    if (!name)
        throw `Name is required in the payload to ${action.type}. Make sure you passed a name to useFilters or wrapped your component in a FilterProvider`;
    if (!draft[name] && action.type !== actions.init)
        throw `State not initialized for ${name}. Render a FilterBar with the name ${name} before trying to modify it.`;
    switch (action.type) {
        case actions.init: {
            init(draft, action);
            break;
        }
        case actions.mergeFilters: {
            const { filters } = action.payload;
            const mergedFilters = {
                ...draft[name].filters,
                ...filters,
            };
            const displayedFilters = getDisplayedFilters(mergedFilters, draft[name].displayedFilters);
            draft[name].filters = mergedFilters;
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.removeAllFilters: {
            const disabledFilters = draft[name].config.filter((conf) => conf.static || conf.disabled);
            draft[name].displayedFilters = disabledFilters.map((filter) => filter.name);
            break;
        }
        case actions.displayAllFilters: {
            const displayedFilters = [];
            draft[name].config.forEach((filter) => {
                displayedFilters.push(filter.name);
            });
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.replaceFilters: {
            const { filters } = action.payload;
            const filterKeys = Object.keys(filters);
            draft[name].config.forEach((filter) => {
                if (filter.required) {
                    if (!filterKeys.includes(filter.name))
                        filters[filter.name] = filters[filter.name] || filter.defaultValues;
                }
            });
            const displayedFilters = getDisplayedFilters(filters, draft[name].displayedFilters);
            draft[name].filters = filters;
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.replaceDisplayedFilters: {
            const { filters } = action.payload;
            const displayedFilters = getDisplayedFilters(filters, []);
            draft[name].filters = filters;
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.displayFilter: {
            if (draft[name].displayedFilters.includes(action.payload.filterName))
                return;
            draft[name].displayedFilters.push(action.payload.filterName);
            break;
        }
        case actions.resetFilters: {
            const displayedFilters = [];
            const filters = {};
            draft[name].config.forEach((filter) => {
                if (filter.defaultValues) {
                    displayedFilters.push(filter.name);
                    filters[filter.name] = filter.defaultValues;
                }
                else {
                    if (filter.static)
                        displayedFilters.push(filter.name);
                }
            });
            draft[name].filters = filters;
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.removeFilter: {
            const displayedFilters = [...draft[name].displayedFilters];
            displayedFilters.splice(displayedFilters.indexOf(action.payload.filterName), 1);
            delete draft[name].filters[action.payload.filterName];
            draft[name].displayedFilters = displayedFilters;
            break;
        }
        case actions.remove: {
            delete draft[name];
            break;
        }
        default:
            console.info({ action });
            throw new Error("Unknown Action 👆");
    }
}
const getDisplayedFilters = (filters, displayedFilters) => {
    const clonedDisplay = [...displayedFilters];
    Object.keys(filters).forEach((filterName) => {
        if (clonedDisplay.includes(filterName))
            return;
        if (!isNilOrEmptyString(filters[filterName])) {
            clonedDisplay.push(filterName);
        }
    });
    return clonedDisplay;
};

export { defaultFilterState, initialState, reducer };
//# sourceMappingURL=reducer.esm.js.map
